import { makeResetStyles, makeStyles, mergeClasses } from '@fluentui/react-components';
import { tokens, typographyStyles } from '@fluentui-copilot/tokens';
import type { SystemMessageSlots, SystemMessageState } from './SystemMessage.types';
import type { GriffelResetStyle, SlotClassNames } from '@fluentui/react-components';

const VISUALLY_HIDDEN = {
  clip: 'rect(0px, 0px, 0px, 0px)',
  height: '1px',
  margin: '-1px',
  width: '1px',
  position: 'absolute',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

export const systemMessageClassNames: SlotClassNames<SystemMessageSlots> = {
  root: 'fai-SystemMessage',
  icon: 'fai-SystemMessage__icon',
  message: 'fai-SystemMessage__message',
  accessibleHeading: 'fai-SystemMessage__accessibleHeading',
};

/**
 * Styles for the root slot
 */
const useStyles = makeStyles({
  root: {
    display: 'grid',
    alignSelf: 'start',
    color: tokens.colorNeutralForeground3,
    columnGap: tokens.spacingHorizontalSNudge,
    ...typographyStyles.caption1,
  },
  withIcon: {
    gridTemplateAreas: `
      "icon   message"
      ".      message"
    `,
    gridTemplateColumns: '16px 1fr',
    gridTemplateRows: `${typographyStyles.caption1.lineHeight} 1fr`,
  },
  noIcon: {
    gridTemplateAreas: `
      "message"
    `,
  },
  sidecar: {
    maxWidth: '288px',
  },
  canvas: {
    maxWidth: '620px',
  },
  icon: {
    gridArea: 'icon',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  message: {
    gridArea: 'message',
  },
});

const useVisuallyHiddenStyles = makeResetStyles(VISUALLY_HIDDEN as GriffelResetStyle);

/**
 * Apply styling to the SystemMessage slots based on the state
 */
export const useSystemMessageStyles_unstable = (state: SystemMessageState): SystemMessageState => {
  'use no memo';

  const styles = useStyles();
  state.root.className = mergeClasses(
    systemMessageClassNames.root,
    styles.root,
    styles[state.mode],
    state.icon ? styles.withIcon : styles.noIcon,
    state.root.className,
  );

  if (state.icon) {
    state.icon.className = mergeClasses(systemMessageClassNames.icon, styles.icon, state.icon.className);
  }

  if (state.message) {
    state.message.className = mergeClasses(systemMessageClassNames.message, styles.message, state.message.className);
  }

  const visuallyHiddenStyles = useVisuallyHiddenStyles();
  if (state.accessibleHeading) {
    state.accessibleHeading.className = mergeClasses(
      systemMessageClassNames.accessibleHeading,
      visuallyHiddenStyles,
      state.accessibleHeading.className,
    );
  }

  return state;
};
